// The write layer for reopening a FAILED cycle — the client half of 0003p, which until now
// existed only as an RPC with no way to call it short of a console.
//
// NOT THE SAME THING AS THE GATE'S "REOPEN". approve_reporting_gate(_decision:'reopen')
// un-signs a gate and rewinds a RENDERED cycle back to verify. It has nothing to say about a
// cycle that died at snapshot or compute with an unsigned gate: there is no signature to
// revoke and no render to rewind. reopen_reporting_cycle is the control for that case, and it
// resumes from current_stage rather than rewinding — the absence of a current_stage write in
// the RPC IS the resume decision (0003p §0.3).
//
// EVERY REFUSAL IS THE SERVER'S. The RPC declines a delivered cycle (on the delivery RECEIPT,
// not just the status label), a signed gate, any status other than 'failed', a reason under 20
// characters, and a cycle already reopened 10 times. The dialog mirrors some of that as UX so
// the operator is not surprised, but the client never decides — it only reports what came back.
//
// REOPENING DOES NOT RUN ANYTHING. 0003p deliberately separates the CONTROL act from the SPEND
// act: reopening clears the failure and restores the status that matches the stage the cycle
// died on, then stops. Queueing the run stays a second, explicit decision through RunCycleControl.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** Mirrors the RPC's own minimum. The server re-checks; this only shapes the UI. */
export const REOPEN_REASON_MIN_CHARS = 20;

export interface ReopenResult {
  reopened: boolean;
  cycle_id: string;
  mode: string;
  status: string;
  current_stage: string;
  stage_seq: number;
  tick_count: number;
  reopen_seq: number;
  reopen_ordinal: number;
  reopen_budget: number;
  next_step?: string;
  [k: string]: unknown;
}

export function useReopenReportingCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ cycleId, reason }: { cycleId: string; reason: string }) => {
      const { data, error } = await (supabase as any).rpc("reopen_reporting_cycle", {
        _cycle_id: cycleId,
        _reason: reason,
      });
      if (error) throw error;
      return data as ReopenResult;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["get_reporting_cycle", vars.cycleId] });
      qc.invalidateQueries({ queryKey: ["list_reporting_cycles"] });
      qc.invalidateQueries({ queryKey: ["get_reporting_cycle_run", vars.cycleId] });
    },
  });
}
